import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home } from "lucide-react";
import { LEAD_STAGE, leadStageToRouteMap } from "../../Utils/constants";
import useLeadStage from "../../hooks/useLeadStage";
import PageLoader from "../../components/Loader";
import FlowFooter from "../../components/FlowFooter";

const curr_page_lead_stage = [
  LEAD_STAGE.BRE_MANUALLY_REJECT,
  LEAD_STAGE.MANUALLY_REJECT,
  LEAD_STAGE.KYC_MANUALLY_REJECT,
  LEAD_STAGE.HARD_REJECT,
];

export default function ApplicationRejected() {
  const { leadStage, isLoadingStage } = useLeadStage();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoadingStage) return;
    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(leadStageToRouteMap[leadStage]);
    }
  }, [leadStage, navigate, isLoadingStage]);

  if (!leadStage) return <PageLoader />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-xl w-full mx-auto flex items-center justify-center min-h-[60vh] space-y-4 sm:space-y-6 py-2 sm:py-4">
          <motion.div
            className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10 w-full"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
          >
            <motion.div
              className="flex justify-center mb-4 sm:mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 border-2 border-red-500 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <svg
                  className="w-6 h-6 sm:w-8 sm:h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </motion.div>

            <motion.div className="text-center" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-[#1E3A8A] mb-2 sm:mb-3">Don't Worry! 🌟</h2>
              <p className="text-xs sm:text-sm md:text-base text-[#64748B] leading-relaxed mb-4 sm:mb-6">
                We believe in second chances! You can always try again with updated information or after some time.
              </p>
            </motion.div>

            <motion.div
              className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-start gap-2 sm:gap-3">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-[#1E293B] mb-1">Need Help?</h3>
                  <p className="text-xs text-[#64748B] leading-relaxed">
                    Our support team is here to help you. Feel free to reach out if you have any questions.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div className="flex justify-center" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}>
              <button
                onClick={() => navigate("/")}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white font-semibold rounded-xl text-xs sm:text-sm md:text-base transition-all hover:opacity-90 shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <Home className="w-4 h-4 sm:w-5 sm:h-5" />
                Back to Home
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <FlowFooter />
    </div>
  );
} 